import React from 'react';
import vars from '@variables';
import RichText from '@components/rich-text/rich-text';
import useWindowSize from '@hooks/use-window-size';
import {
  CallToAction,
  Content,
  ExternalCallToAction,
  Image,
  Title,
  Wrapper,
  Background,
  VideoWrapper,
} from './single-cta-block.styles';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { getGtmLinkId } from '@utils/utils';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { SingleCtaBlockProps, ALIGN_CONTENT } from './single-cta-block.types';

interface ImageHandlerProps {
  imageSrc: string | null;
  mobileImageSrc: string | null;
  imageAlt: string;
  format: string;
  mobileFormat: string;
  gatsbyBackgroundImage: any;
  gatsbyMobileBackgroundImage: any;
  backgroundColor: string | null;
  isMobile: boolean;
}

const ImageHandler: React.FC<ImageHandlerProps> = ({
  imageSrc,
  mobileImageSrc,
  imageAlt,
  format,
  mobileFormat,
  gatsbyBackgroundImage,
  gatsbyMobileBackgroundImage,
  backgroundColor,
  isMobile,
}) => {
  if (isMobile) {
    if (mobileImageSrc && gatsbyMobileBackgroundImage) {
      return (
        <GatsbyImage
          data-testid="mobileBgImage"
          image={gatsbyMobileBackgroundImage}
          alt={imageAlt}
          className="gatsby-image-pos"
        />
      );
    } else if (
      mobileImageSrc &&
      (mobileFormat === 'image/gif' || !gatsbyMobileBackgroundImage)
    ) {
      return (
        <Image
          data-testid="mobileBgFallback"
          src={mobileImageSrc}
          alt={imageAlt}
        ></Image>
      );
    } else if (!mobileImageSrc && backgroundColor) {
      return <Background data-testid="background" bgColor={backgroundColor} />;
    } else {
      return null;
    }
  } else {
    if (imageSrc && gatsbyBackgroundImage) {
      return (
        <GatsbyImage
          data-testid="bgImage"
          image={gatsbyBackgroundImage}
          alt={imageAlt}
          className="gatsby-image-pos"
        />
      );
    } else if (imageSrc && (format === 'image/gif' || !gatsbyBackgroundImage)) {
      return (
        <Image data-testid="bgFallback" src={imageSrc} alt={imageAlt}></Image>
      );
    } else if (!imageSrc && !backgroundColor) {
      return null;
    } else if (!imageSrc && backgroundColor) {
      return <Background data-testid="background" bgColor={backgroundColor} />;
    }
  }
};

const SingleCtaBlock: React.FC<SingleCtaBlockProps> = ({
  title,
  content,
  imageSrc = null,
  mobileImageSrc = null,
  imageAlt = '',
  format = vars.defaultImageFormat,
  mobileFormat = vars.defaultImageFormat,
  gatsbyImageData,
  gatsbyMobileImageData,
  backgroundColor = null,
  backgroundVideoUrl,
  videoBackgroundImage,
  alignment = ALIGN_CONTENT.CENTRE,
  linkOnePath,
  linkOneText,
  isLinkOneExternal = false,
  linkTwoPath,
  linkTwoText,
  isLinkTwoExternal = false,
  showWhiteBox = false,
  useLightText = false,
}) => {
  const size = useWindowSize();
  const gatsbyBackgroundImage = getImage(gatsbyImageData);
  const gatsbyMobileBackgroundImage = getImage(gatsbyMobileImageData);

  const isMobile = !!size.width && size.width <= 768;

  const showVideo = !!backgroundVideoUrl;

  return (
    <Wrapper>
      {showVideo ? (
        <VideoWrapper data-testid="video">
          <video autoPlay loop muted playsInline src={backgroundVideoUrl} />
          <div className="video-image-bg">
            {videoBackgroundImage?.file && (
              <img
                src={videoBackgroundImage.file.url}
                alt={videoBackgroundImage.description || ''}
              />
            )}
          </div>
        </VideoWrapper>
      ) : (
        <ImageHandler
          imageSrc={imageSrc}
          mobileImageSrc={mobileImageSrc}
          imageAlt={imageAlt}
          format={format}
          mobileFormat={mobileFormat}
          gatsbyBackgroundImage={gatsbyBackgroundImage}
          gatsbyMobileBackgroundImage={gatsbyMobileBackgroundImage}
          backgroundColor={backgroundColor}
          isMobile={isMobile}
        />
      )}
      <Content
        data-testid="content"
        alignment={alignment}
        showWhiteBox={showWhiteBox}
        useLightText={useLightText}
      >
        {title && <Title useLightText={useLightText}>{title}</Title>}
        {content && content.raw && <RichText content={content} />}
        {linkOnePath && linkOneText && (
          <>
            {isLinkOneExternal ? (
              <ExternalCallToAction
                data-gtm-id={`cta-button-${getGtmLinkId(linkOnePath)}`}
                href={linkOnePath}
                target="_blank"
                useLightText={useLightText}
              >
                {linkOneText}
              </ExternalCallToAction>
            ) : (
              <CallToAction
                data-gtm-id={GTM_ID.CTA_BUTTON_INTERNAL_LINK}
                to={linkOnePath}
                useLightText={useLightText}
              >
                {linkOneText}
              </CallToAction>
            )}
          </>
        )}
        {linkTwoPath && linkTwoText && (
          <>
            {isLinkTwoExternal ? (
              <ExternalCallToAction
                data-gtm-id={`cta-button-${getGtmLinkId(linkTwoPath)}`}
                href={linkTwoPath}
                target="_blank"
                useLightText={useLightText}
              >
                {linkTwoText}
              </ExternalCallToAction>
            ) : (
              <CallToAction
                data-gtm-id={GTM_ID.CTA_BUTTON_INTERNAL_LINK}
                to={linkTwoPath}
                useLightText={useLightText}
              >
                {linkTwoText}
              </CallToAction>
            )}
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default SingleCtaBlock;
